import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'

export type Theme = 'light' | 'dark'

interface ThemeValue {
    theme: Theme
    setTheme: (theme: Theme) => void
    toggleTheme: () => void
}

interface ThemeProviderProps {
    children: ReactNode
}

const STORAGE_KEY = 'atalaya-theme'
const ThemeContext = createContext<ThemeValue | null>(null)

/**
 * Light by default on the server and on first paint; the saved choice (or the
 * system preference) is applied after mount, so markup never diverges.
 */
export const ThemeProvider: FC<ThemeProviderProps> = ({ children }): ReactNode => {
    const [theme, setThemeState] = useState<Theme>('light')

    useEffect(() => {
        let saved: string | null = null

        try {
            saved = window.localStorage.getItem(STORAGE_KEY)
        }
        catch {
            saved = null
        }

        if (saved === 'dark' || saved === 'light') setThemeState(saved)
        else if (window.matchMedia('(prefers-color-scheme: dark)').matches) setThemeState('dark')
    }, [])

    useEffect(() => {
        const root = document.documentElement

        root.classList.toggle('dark', theme === 'dark')
        root.style.colorScheme = theme

        const meta = document.querySelector('meta[name="theme-color"]')

        if (meta) meta.setAttribute('content', theme === 'dark' ? '#0f1117' : '#f4f5f8')
    }, [theme])

    const setTheme = useCallback((next: Theme) => {
        setThemeState(next)

        try {
            window.localStorage.setItem(STORAGE_KEY, next)
        }
        catch {
            // Storage can be unavailable (private mode); the choice still applies
        }
    }, [])

    const toggleTheme = useCallback(() => setTheme(theme === 'dark' ? 'light' : 'dark'), [theme, setTheme])

    const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme])

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    )
}

export const useTheme = (): ThemeValue => {
    const value = useContext(ThemeContext)

    if (!value) throw new Error('useTheme must be used inside ThemeProvider')

    return value
}
